import { useMemo, useState } from 'react';
import { Can } from '@/components/common/Can';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyValue } from '@/components/common/EmptyValue';
import { TableScrollFrame, WrappingCellText } from '@/components/common/DataTable';
import { STICKY_TABLE_HEADER_CLASS } from '@/components/common/DataTable/dataTable.constants';
import { DataTableEmpty } from '@/components/common/DataTable/DataTableEmpty';
import { DataTableLoading } from '@/components/common/DataTable/DataTableLoading';
import { InfiniteLoadingRow } from '@/components/common/DataTable/InfiniteLoadingRow';
import { RowActionButton } from '@/components/common/RowActions';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { groupByDistrict } from '../utils/groupByDistrict';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRemoveInstitutes } from '../hooks/useRemoveInstitutes';
import type { AssignedInstituteItem } from '../types/userInstituteMapping.types';

interface AssignedInstitutesTableProps {
  data: AssignedInstituteItem[];
  isLoading: boolean;
  userId: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
}

export function AssignedInstitutesTable({
  data,
  isLoading,
  userId,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
}: AssignedInstitutesTableProps) {
  const [toRemove, setToRemove] = useState<AssignedInstituteItem | null>(null);
  const { mutate, isPending } = useRemoveInstitutes(() => setToRemove(null));

  const groups = useMemo(() => groupByDistrict(data), [data]);

  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({
    onLoadMore,
    hasMore: hasMore && !loadMoreError,
    isLoadingMore,
  });

  function handleConfirm() {
    if (!toRemove) return;
    mutate({ userId, instituteIds: [toRemove.id] });
  }

  return (
    <>
      <TableScrollFrame className="lg:min-h-0 lg:flex-1">
        <table className="w-full min-w-160 table-fixed caption-bottom text-sm">
          <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[15%]">District</TableHead>
              <TableHead className="w-[34%]">Institute Name</TableHead>
              <TableHead className="w-[18%]">Institute Type</TableHead>
              <TableHead className="w-[17%]">Thana</TableHead>
              <TableHead className="w-[16%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <DataTableLoading columnCount={5} rowCount={5} />
            ) : groups.length === 0 ? (
              <DataTableEmpty
                columnCount={5}
                message="No assigned institute matches"
                hint="Try other filters."
              />
            ) : (
              <>
                {groups.flatMap((group) =>
                  group.institutes.map((institute, index) => (
                    <TableRow
                      key={`${group.districtName}-${institute.id}`}
                      className={cn(
                        'border-b border-border/50 bg-card',
                        index === group.institutes.length - 1 && 'border-b-2',
                      )}
                    >
                      <TableCell className="align-top font-medium">
                        <WrappingCellText width="fill">
                          {index === 0 ? group.districtName : ''}
                        </WrappingCellText>
                      </TableCell>
                      <TableCell>
                        <WrappingCellText width="fill">{institute.name}</WrappingCellText>
                      </TableCell>
                      <TableCell>
                        <WrappingCellText width="fill">
                          {institute.instituteTypeName ?? <EmptyValue />}
                        </WrappingCellText>
                      </TableCell>
                      <TableCell>
                        <WrappingCellText width="fill">
                          {institute.thana?.name ?? <EmptyValue />}
                        </WrappingCellText>
                      </TableCell>
                      <TableCell>
                        <Can code={RBAC_PERMISSIONS.INSTITUTE_MAPPING_DELETE}>
                          <RowActionButton
                            label="Remove"
                            ariaLabel={`Remove ${institute.name}`}
                            tone="destructive"
                            onClick={() => setToRemove(institute)}
                          />
                        </Can>
                      </TableCell>
                    </TableRow>
                  )),
                )}
                {onLoadMore && (hasMore || loadMoreError) ? (
                  <InfiniteLoadingRow
                    ref={sentinelRef}
                    columnCount={5}
                    isLoading={isLoadingMore}
                    hasError={loadMoreError}
                    onRetry={onLoadMore}
                  />
                ) : null}
              </>
            )}
          </TableBody>
        </table>
      </TableScrollFrame>

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title="Remove institute?"
        description={
          toRemove
            ? `"${toRemove.name}" will be removed from this user. This action cannot be undone.`
            : ''
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={isPending}
        pendingLabel="Removing..."
        onConfirm={handleConfirm}
      />
    </>
  );
}
